import { SettingsManager } from "@viamap/viamap2-common";
import { getCognitoIdToken } from "./CognitoIdToken";

/** "etl": tinglysning Lambdas (viamap2-etl-service). "dll": dll-service Lambdas. "es": Elasticsearch. */
export type GatewayService = "etl" | "dll" | "es";

export type GatewayRequest = {
   method?: "GET" | "POST" | "PUT" | "DELETE" | "HEAD";
   /** Sent as JSON. */
   body?: unknown;
   /** Sent as is, for bodies that are already serialized. */
   rawBody?: string;
   query?: Record<string, string>;
   signal?: AbortSignal;
};

/**
 * Calls a Lambda or Elasticsearch through viamap-gatewayproxy, which verifies the user's Cognito ID token and adds
 * the key server side. Calls without a logged-in user only succeed for the proxy's public endpoints.
 * `endpoint` may already contain a query string.
 */
export async function gatewayFetch(service: GatewayService, endpoint: string, request: GatewayRequest = {}): Promise<Response> {
   const baseUrl = String(SettingsManager.getSystemSetting("gatewayProxyUrl", "") || "").trim().replace(/\/+$/, "");
   if (!baseUrl) {
      throw new Error("Missing gatewayProxyUrl setting");
   }
   const path = endpoint.replace(/^\/+/, "");
   const query = request.query ? (path.includes("?") ? "&" : "?") + new URLSearchParams(request.query).toString() : "";
   const url = `${baseUrl}/${service}/${path}${query}`;
   const body = request.rawBody ?? (request.body !== undefined ? JSON.stringify(request.body) : undefined);

   const send = (token: string | undefined) => fetch(url, {
      method: request.method ?? "POST",
      credentials: "omit",
      headers: {
         ...(body !== undefined && { "Content-Type": "application/json" }),
         ...(token && { Authorization: `Bearer ${token}` }),
      },
      body,
      signal: request.signal,
   });

   const token = await getCognitoIdToken();
   const response = await send(token);
   if (response.status === 401 && token) {
      const refreshedToken = await getCognitoIdToken({ forceRefresh: true });
      if (refreshedToken && refreshedToken !== token) {
         return send(refreshedToken);
      }
   }
   return response;
}
